import { useEffect, useState, useCallback, useRef, type ReactNode } from "react"
import {
  useDaily,
  useDevices,
  useLocalParticipant,
  useParticipantIds,
  useDailyEvent,
  useScreenShare,
  DailyVideo,
  DailyAudio,
} from "@daily-co/daily-react"
import { toast } from "sonner"
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  MonitorUp,
  MonitorOff,
  PhoneOff,
  Users,
  Loader2,
  AlertCircle,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { DeviceSettingsMenu } from "./DeviceSettingsMenu"
import { applyStoredDevicePreferences } from "./devicePreferences"
import { PRACTICE_ROOM_MINUTES } from "./practiceSeed"

const roundedVideoStyle: React.CSSProperties = {
  borderRadius: "0.5rem",
  WebkitMaskImage: "-webkit-radial-gradient(white, black)",
}

interface DailyVideoPanelProps {
  roomUrl: string
  token: string
  userName: string
  onLeave?: () => void
  stacked?: boolean
  // Solo practice room (#945): there is never a second participant, so show the
  // LOCAL camera as the main tile instead of waiting-for-the-other-person copy.
  soloRoom?: boolean
  className?: string
  // The phone classroom (#1523): the video is a strip at the top, `children` (the
  // tabs) fill the middle and the call buttons sit in a bar at the bottom, so the
  // panel is the whole screen under the header. Chosen once per lesson, see
  // usePhoneLayout.
  phone?: boolean
  children?: ReactNode
}

// Screen sharing needs getDisplayMedia, which phone browsers do not have; the
// button is left out where it could only fail (#1523, #1099).
const canShareScreen = () => typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getDisplayMedia === "function"

// The height of the video strip on a phone: a quarter of the screen, never less
// than two tiles side by side need.
const PHONE_STRIP = "h-[24dvh] min-h-28 shrink-0"

type CallState = "joining" | "connected" | "left" | "error" | "ended"
type LeaveReason = "intentional" | "network" | "error" | "unknown"

export function DailyVideoPanel({
  roomUrl,
  token,
  userName,
  onLeave,
  stacked = false,
  soloRoom = false,
  className,
  phone = false,
  children,
}: DailyVideoPanelProps) {
  const { t } = useTranslation()
  const daily = useDaily()
  const localParticipant = useLocalParticipant()
  const remoteIds = useParticipantIds({ filter: "remote" })
  const { isSharingScreen, startScreenShare, stopScreenShare } = useScreenShare()
  const { currentCam, currentMic, setCamera, setMicrophone } = useDevices()

  const [callState, setCallState] = useState<CallState>("joining")
  const [isCameraOn, setIsCameraOn] = useState(true)
  const [isMicOn, setIsMicOn] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const hasSeenRemoteRef = useRef(false)
  // Whether we ever reached "connected" — a camera/mic failure BEFORE connecting
  // is a permission/device denial worth a distinct actionable message; one after
  // connecting is transient and must not tear down a live call.
  const hasConnectedRef = useRef(false)
  const isIntentionalLeaveRef = useRef(false)
  const lastLeaveReasonRef = useRef<LeaveReason>("unknown")
  const didAutoRejoinRef = useRef(false)
  // The user's INTENT for their devices, tracked across reconnects. Daily defaults
  // devices ON for each join, so without re-applying intent a rejoin / "try again"
  // would silently turn a deliberately-off camera or mic back on.
  const cameraIntentRef = useRef(true)
  const micIntentRef = useRef(true)

  useEffect(() => {
    if (remoteIds.length > 0) hasSeenRemoteRef.current = true
  }, [remoteIds.length])

  // Every join routes through here so the camera/mic come up in the user's
  // intended state (not Daily's on-by-default) on the first join AND every rejoin.
  const joinWithIntent = useCallback(() => {
    return daily?.join({
      url: roomUrl,
      token,
      userName,
      startVideoOff: !cameraIntentRef.current,
      startAudioOff: !micIntentRef.current,
    })
  }, [daily, roomUrl, token, userName])

  useEffect(() => {
    if (!daily || !roomUrl || !token) return

    joinWithIntent()?.catch((err) => {
      console.error("Failed to join Daily call:", err)
      setErrorMessage(t("liveSession.errors.connect"))
      setCallState("error")
    })

    return () => {
      daily.leave().catch(() => {})
    }
  }, [daily, roomUrl, token, joinWithIntent])

  useDailyEvent("joined-meeting", () => {
    setCallState("connected")
    hasConnectedRef.current = true
    didAutoRejoinRef.current = false
    lastLeaveReasonRef.current = "unknown"
    // A fresh join has no remote yet; let the remoteIds effect re-set this so the
    // waiting copy reads "Waiting…" not "Other participant left".
    hasSeenRemoteRef.current = false
    // Re-assert device intent — Daily defaults devices ON per join.
    daily?.setLocalVideo(cameraIntentRef.current)
    daily?.setLocalAudio(micIntentRef.current)
    // Re-apply the remembered camera/mic/speaker too: Daily re-acquires
    // defaults per join, so without this a rejoin lands back on the wrong
    // camera — the very failure #940 is about, just mid-lesson.
    if (daily) void applyStoredDevicePreferences(daily)
  })

  useDailyEvent("left-meeting", () => {
    setCallState("left")
    if (isIntentionalLeaveRef.current) {
      lastLeaveReasonRef.current = "intentional"
      onLeave?.()
    }
  })

  useDailyEvent("error", (event) => {
    console.error("Daily error:", event)
    // Room/token expiry or an eject is terminal — the room is gone, so a retry
    // would only fail. Show a final "ended" state with no Rejoin.
    const type = (event as { error?: { type?: string } }).error?.type
    if (type && ["exp-room", "exp-token", "nbf-room", "nbf-token", "ejected"].includes(type)) {
      setCallState("ended")
      return
    }
    lastLeaveReasonRef.current = "error"
    setErrorMessage(t("liveSession.errors.connection"))
    setCallState("error")
  })

  // getUserMedia failed — most commonly the user denied camera/mic permission,
  // or no device is available. If it happens before we ever connect, surface an
  // actionable permission message instead of a generic "connection error"; once
  // connected we let the call continue (Daily just shows no local video).
  useDailyEvent("camera-error", (event) => {
    console.error("Daily camera error:", event)
    if (!hasConnectedRef.current) {
      setErrorMessage(t("liveSession.errors.devicePermission"))
      setCallState("error")
    }
  })

  // Track the ids in refs: the event handler below must compare against
  // what is selected NOW, not what was selected when it was registered.
  const currentCamIdRef = useRef<string | undefined>(undefined)
  const currentMicIdRef = useRef<string | undefined>(undefined)
  currentCamIdRef.current = currentCam?.device.deviceId
  currentMicIdRef.current = currentMic?.device.deviceId

  // A device in use was unplugged (webcam pulled, AirPods case closed):
  // switch to a remaining one and say so, instead of leaving a dead tile.
  // When Daily already auto-switched (the current id is still listed) or
  // nothing of that kind is left (camera-error tells that story), stay quiet.
  useDailyEvent("available-devices-updated", (event) => {
    const available =
      (event as { availableDevices?: MediaDeviceInfo[] }).availableDevices ?? []
    const fallBack = (
      kind: "videoinput" | "audioinput",
      currentId: string | undefined,
      apply: (id: string) => Promise<void> | void,
      messageKey: string,
    ) => {
      if (!currentId) return
      const ofKind = available.filter((d) => d.kind === kind && d.deviceId !== "")
      if (ofKind.some((d) => d.deviceId === currentId)) return
      const next = ofKind[0]
      if (!next) return
      // Toast only once the switch actually succeeded — telling someone
      // "switched to X" while their tile stays dead is worse than silence.
      Promise.resolve(apply(next.deviceId))
        .then(() => toast.info(t(messageKey, { device: next.label || next.deviceId })))
        .catch(() => {})
    }
    fallBack("videoinput", currentCamIdRef.current, setCamera, "liveSession.devices.cameraSwitched")
    fallBack("audioinput", currentMicIdRef.current, setMicrophone, "liveSession.devices.microphoneSwitched")
  })

  useDailyEvent("network-connection", (event) => {
    const state = (event as { event?: string }).event
    if (state && state !== "connected") {
      lastLeaveReasonRef.current = "network"
    }
  })

  useEffect(() => {
    if (localParticipant) {
      const cam = localParticipant.video ?? false
      const mic = localParticipant.audio ?? false
      setIsCameraOn(cam)
      setIsMicOn(mic)
      // Keep intent in sync with the actual device state so the next (re)join
      // restores what the user currently has, not Daily's on-by-default.
      cameraIntentRef.current = cam
      micIntentRef.current = mic
    }
  }, [localParticipant?.video, localParticipant?.audio])

  const toggleCamera = useCallback(() => {
    const next = !isCameraOn
    cameraIntentRef.current = next
    daily?.setLocalVideo(next)
  }, [daily, isCameraOn])

  const toggleMic = useCallback(() => {
    const next = !isMicOn
    micIntentRef.current = next
    daily?.setLocalAudio(next)
  }, [daily, isMicOn])

  const toggleScreenShare = useCallback(() => {
    if (isSharingScreen) {
      stopScreenShare()
    } else {
      startScreenShare()
    }
  }, [isSharingScreen, startScreenShare, stopScreenShare])

  const leaveCall = useCallback(() => {
    isIntentionalLeaveRef.current = true
    lastLeaveReasonRef.current = "intentional"
    daily?.leave()
  }, [daily])

  const rejoinCall = useCallback(() => {
    if (!daily) return
    isIntentionalLeaveRef.current = false
    didAutoRejoinRef.current = false // a manual rejoin re-arms auto-recovery
    setCallState("joining")
    setErrorMessage(null)
    joinWithIntent()?.catch(() => {
      setErrorMessage(t("liveSession.errors.reconnect"))
      setCallState("error")
    })
  }, [daily, joinWithIntent, t])

  useEffect(() => {
    const onOnline = () => {
      if (
        callState === "left" &&
        lastLeaveReasonRef.current === "network" &&
        !didAutoRejoinRef.current
      ) {
        didAutoRejoinRef.current = true
        rejoinCall()
      }
    }
    window.addEventListener("online", onOnline)
    return () => window.removeEventListener("online", onOnline)
  }, [callState, rejoinCall])

  // Belt-and-suspenders for hard network drops where the browser 'online' event
  // never fires (connected-but-no-internet, captive portal): once we land in the
  // network-drop state, attempt a single rejoin after a short delay.
  useEffect(() => {
    if (callState !== "left" || lastLeaveReasonRef.current !== "network" || didAutoRejoinRef.current) {
      return
    }
    const timer = setTimeout(() => {
      if (!didAutoRejoinRef.current) {
        didAutoRejoinRef.current = true
        rejoinCall()
      }
    }, 3000)
    return () => clearTimeout(timer)
  }, [callState, rejoinCall])

  // The states without a call. On a phone the message takes the video strip and
  // the tabs stay below it, so the board and the chat are still there while the
  // call reconnects, as they are beside the message on a desktop.
  const unconnected = (node: ReactNode) =>
    phone ? (
      <div className={cn("flex flex-col h-full", className)}>
        <div className={cn(PHONE_STRIP, "overflow-y-auto")}>{node}</div>
        {children}
      </div>
    ) : (
      node
    )

  if (callState === "joining") {
    return unconnected(
      <div className={cn("flex flex-col h-full items-center justify-center gap-3", !phone && className)}>
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">{t("liveSession.connecting")}</span>
      </div>,
    )
  }

  if (callState === "error") {
    return unconnected(
      <div className={cn("flex flex-col h-full items-center justify-center gap-3", !phone && className)}>
        <AlertCircle className="w-8 h-8 text-destructive" />
        <span className="text-sm text-destructive text-center px-4">{errorMessage}</span>
        <Button variant="outline" size="sm" onClick={rejoinCall}>
          {t("liveSession.tryAgain")}
        </Button>
      </div>,
    )
  }

  if (callState === "ended") {
    return unconnected(
      <div className={cn("flex flex-col h-full items-center justify-center gap-3", !phone && className)}>
        <Users className="w-8 h-8 text-muted-foreground" />
        <span className="text-sm text-muted-foreground text-center px-4">
          {/* A practice room self-expires (Daily ejects at exp); "this session has
              ended" reads like a breakage there, so say what actually happened. */}
          {soloRoom
            ? t("liveSession.practice.endedSolo", { minutes: PRACTICE_ROOM_MINUTES })
            : t("liveSession.ended")}
        </span>
        {onLeave && (
          <Button variant="outline" size="sm" onClick={onLeave}>
            {t("liveSession.backToClassroom")}
          </Button>
        )}
      </div>,
    )
  }

  if (callState === "left") {
    const reason = lastLeaveReasonRef.current
    const isNetworkDrop = reason === "network"
    const label =
      reason === "network" ? t("liveSession.left.network")
      : reason === "error" ? t("liveSession.left.error")
      : t("liveSession.left.intentional")
    const Icon = isNetworkDrop ? Loader2 : Users
    return unconnected(
      <div className={cn("flex flex-col h-full items-center justify-center gap-3", !phone && className)}>
        <Icon className={cn("w-8 h-8", isNetworkDrop ? "animate-spin text-primary" : "text-muted-foreground")} />
        <span className="text-sm text-muted-foreground">{label}</span>
        {/* Always offer a manual Rejoin — even on a network drop, where the spinner
            alone would otherwise strand the user if auto-recovery never fires. */}
        <Button variant="outline" size="sm" onClick={rejoinCall}>
          {t("liveSession.rejoin")}
        </Button>
      </div>,
    )
  }

  const remoteId = remoteIds[0]
  // A solo room never gets a peer: it's the local user's own tile that matters.
  const soloAlone = soloRoom && !remoteId
  const waitingMessage = soloRoom
    ? t("liveSession.practice.solo")
    : hasSeenRemoteRef.current
      ? t("liveSession.waiting.left")
      : t("liveSession.waiting.initial")

  // The tiles. A phone strip is short and wide, so its two tiles sit side by side.
  const videoArea = (
    <>
      {stacked || phone ? (
        <div className={cn("flex-1 flex gap-2 p-2 overflow-hidden min-h-0", phone ? "flex-row" : "flex-col")}>
          {!soloAlone && (
            <div className="flex-1 rounded-lg overflow-hidden bg-muted relative min-h-0">
              {remoteId ? (
                <DailyVideo
                  sessionId={remoteId}
                  type="video"
                  fit="cover"
                  automirror
                  style={roundedVideoStyle}
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground px-2 text-center">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                    <Users className="w-6 h-6" />
                  </div>
                  <span className="text-xs">{waitingMessage}</span>
                </div>
              )}
            </div>
          )}
          <div className="flex-1 rounded-lg overflow-hidden bg-muted relative min-h-0">
            {localParticipant && isCameraOn ? (
              <DailyVideo
                sessionId={localParticipant.session_id}
                type="video"
                fit="cover"
                mirror
                style={roundedVideoStyle}
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground px-2 text-center">
                <Users className="w-8 h-8" />
                {soloAlone && <span className="text-xs">{waitingMessage}</span>}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center overflow-hidden relative min-h-0">
          {remoteId ? (
            <DailyVideo
              sessionId={remoteId}
              type="video"
              fit="cover"
              automirror
              style={roundedVideoStyle}
              className="w-full h-full"
            />
          ) : soloAlone && localParticipant && isCameraOn ? (
            <DailyVideo
              sessionId={localParticipant.session_id}
              type="video"
              fit="cover"
              mirror
              style={roundedVideoStyle}
              className="w-full h-full"
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-muted-foreground px-4 text-center">
              <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center">
                <Users className="w-10 h-10" />
              </div>
              <span className="text-sm">{waitingMessage}</span>
            </div>
          )}

          {localParticipant && !soloAlone && (
            <div className="absolute bottom-0 right-0 h-24 max-w-[40%] rounded-lg overflow-hidden shadow-card bg-muted">
              {isCameraOn ? (
                <DailyVideo
                  sessionId={localParticipant.session_id}
                  type="video"
                  fit="cover"
                  mirror
                  style={roundedVideoStyle}
                  className="h-full w-auto"
                />
              ) : (
                <div className="h-full aspect-video flex items-center justify-center">
                  <Users className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  )

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {phone ? <div className={cn(PHONE_STRIP, "flex flex-col")}>{videoArea}</div> : videoArea}
      {phone && children}

      <DailyAudio />

      {/* On a phone the bar is always on screen, below the tabs and above the
          home indicator, and its buttons are big enough for a thumb: leave is
          the last one, away from the microphone. */}
      <div
        className={cn(
          "flex items-center justify-center shrink-0",
          phone ? "gap-4 px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] border-t border-border bg-card" : "gap-3 py-4",
        )}
      >
        <Button
          variant={isMicOn ? "default" : "secondary"}
          size="icon"
          onClick={toggleMic}
          className={cn("rounded-full", phone && "size-11")}
          aria-label={t("liveSession.devices.microphone")}
          aria-pressed={isMicOn}
        >
          {isMicOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </Button>
        <Button
          variant={isCameraOn ? "default" : "secondary"}
          size="icon"
          onClick={toggleCamera}
          className={cn("rounded-full", phone && "size-11")}
          aria-label={t("liveSession.devices.camera")}
          aria-pressed={isCameraOn}
        >
          {isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </Button>
        {canShareScreen() && (
          <Button
            variant={isSharingScreen ? "default" : "secondary"}
            size="icon"
            onClick={toggleScreenShare}
            className={cn("rounded-full", phone && "size-11")}
            aria-label={t("liveSession.screenShare")}
            aria-pressed={isSharingScreen}
          >
            {isSharingScreen ? <MonitorOff className="w-5 h-5" /> : <MonitorUp className="w-5 h-5" />}
          </Button>
        )}
        <DeviceSettingsMenu />
        <Button
          variant="destructive"
          size="icon"
          onClick={leaveCall}
          className={cn("rounded-full", phone && "size-11")}
          aria-label={t("liveSession.leave")}
        >
          <PhoneOff className="w-5 h-5" />
        </Button>
      </div>
    </div>
  )
}

export function ScreenShareView({ className }: { className?: string }) {
  const { screens } = useScreenShare()
  const localParticipant = useLocalParticipant()
  const localSessionId = localParticipant?.session_id

  const screen =
    screens.find((s) => s.session_id !== localSessionId) ??
    screens.find((s) => s.session_id === localSessionId)

  if (!screen) return null

  return (
    <DailyVideo
      sessionId={screen.session_id}
      type="screenVideo"
      style={roundedVideoStyle}
      className={cn("w-full h-full bg-muted", className)}
    />
  )
}

export function useIsScreenSharing(): boolean {
  const { screens } = useScreenShare()
  return screens.length > 0
}
